import { useEffect, useState } from "react";
import moment from "moment";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../styled-system/jsx";
import { Accordion } from "../../core/basic/Accordion";
import { Button } from "../../core/basic/Button";
import { ExplainerBox } from "../../core/basic/ExplainerBox";
import { Modal } from "../../core/basic/Modal";
import { useSimpleCan } from "../../../config/casl/casl";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { getOrganizationsOptions } from "../../../modules/admin/api/useOrganizations";
import { useAppSelector } from "../../../redux/hooks";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { errorToast, successToast } from "../../../utils/toasts";
import { apiClient } from "../../../zodios/client";
import { AMCInstanceSection } from "../../../modules/amc/components/instance/AMCInstanceSection";
import { AddAmazonProfilesDialogue } from "./AddAmazonProfilesDialogue";
import { ImportDSPClientsDialogue } from "./ImportDSPClientsDialogue";
import { MissingProfilesSections } from "./MissingProfilesSections";
import ProfileHealthSection from "./ProfileHealthSection";
import { RemoteAmazonProfilesTable } from "./RemoteAmazonProfilesTable";
import { TrellisProfilesTable } from "./TrellisProfilesTable";
import { useAmazonAdsConnectionStatus } from "./useAmazonAdsConnectionStatus";
import { type AmazonProfile, toProfileSelections } from "./useAmazonProfiles";

// The modal keeps its default max height and scrolls, so a long table page can't push its buttons off-screen.
const modalCss = { width: "60vw", overflowY: "auto" } as const;

const paragraphCss = css({ fontSize: "large" });

export function AmazonProfileSummary() {
  const { t } = useTranslation(["onboarding"]);
  const fmt = useDateFormatter("long");
  const [showRemoteProfiles, setShowRemoteProfiles] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDSPImportModal, setShowDSPImportModal] = useState(false);

  const user = useAppSelector((state) => state.user);
  const dspClientsUIEnabled = useModuleEnabled("DSP_CLIENTS_UI");
  const userCanImportDSPClients = useSimpleCan("manage", "DSPEntity") && user.is_staff && dspClientsUIEnabled;
  const adsConnectionStatus = useAmazonAdsConnectionStatus();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const isImportModalOpen = showImportModal || search.modal === "profileImport";

  const setImportModalOpen = (isOpen: boolean) => {
    setShowImportModal(isOpen);
    if (!isOpen && search.modal !== undefined) {
      void navigate({ to: ".", search: (prev) => ({ ...prev, modal: undefined }), replace: true, resetScroll: false });
    }
  };

  // OAuth redirects land here with `?notification=…`; toast it once, then drop the param.
  const notificationType = search.notification;
  useEffect(() => {
    const messages: Record<string, string> = {
      additionalAccountSuccessfullyAdded: t(($) => $.onboarding["New Amazon Ads account successfully authorized."]),
      reauthSuccess: t(($) => $.onboarding["Amazon Ads account successfully reauthorized."]),
      spApiReauthSuccess: t(($) => $.onboarding["SP-API account successfully reauthorized."]),
    };
    if (!notificationType || !Object.hasOwn(messages, notificationType)) {
      return;
    }
    const timeout = setTimeout(() => {
      successToast({ message: messages[notificationType], options: { toastId: notificationType } });
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, notification: undefined }),
        replace: true,
        resetScroll: false,
      });
    }, 2000);
    return () => clearTimeout(timeout);
  }, [notificationType, navigate, t]);

  const trellisProfilesQuery = useQuery(getOrganizationsOptions({ channel: "AMAZON" }));

  const importProfiles = useMutation({
    mutationFn: async (profiles: AmazonProfile[]) =>
      await apiClient.importAmazonProfiles({
        org: user.organization_id,
        selected_profiles: toProfileSelections(profiles),
      }),
    onSuccess: () => {
      successToast({ message: t(($) => $.onboarding["Successfully Refreshed Amazon Profiles"]) });
      setImportModalOpen(false);
      // The import runs as a background task, so give it time to create the new organizations.
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: [{ entity: "organizations" }] });
      }, 10000);
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.onboarding["Error Refreshing Amazon Profiles"]), error });
    },
  });

  const authorizedBy = adsConnectionStatus.data?.amazon_ads_api_authorized_by;
  const authorizedOn = adsConnectionStatus.data?.amazon_ads_api_authorized_on;

  return (
    <>
      <HStack justifyContent="flex-end" gap="medium">
        <Button isDisabled={importProfiles.isPending} onPress={() => setImportModalOpen(true)}>
          {t(($) => $.onboarding["Add Profiles"])}
        </Button>
      </HStack>
      <Modal
        isOpen={isImportModalOpen}
        onOpenChange={setImportModalOpen}
        title={t(($) => $.onboarding["Import Additional Amazon Profiles"])}
        css={modalCss}
      >
        <AddAmazonProfilesDialogue
          onClose={() => setImportModalOpen(false)}
          onSubmit={(profiles) => importProfiles.mutate(profiles)}
          isSubmitting={importProfiles.isPending}
          justConnectedAccount={notificationType === "additionalAccountSuccessfullyAdded"}
        />
      </Modal>
      {userCanImportDSPClients && (
        <Modal
          isOpen={showDSPImportModal}
          onOpenChange={setShowDSPImportModal}
          title={t(($) => $.onboarding["Import DSP Profiles"])}
          css={modalCss}
        >
          <ImportDSPClientsDialogue isOpen={showDSPImportModal} setShowModal={setShowDSPImportModal} />
        </Modal>
      )}

      <Accordion title={t(($) => $.onboarding["Trellis Profile Summary"])}>
        <VStack alignItems="stretch" gap="medium">
          <p className={paragraphCss}>
            {t(
              ($) =>
                $.onboarding["Below is a summary of all Amazon profiles currently connected to your Trellis account."],
            )}
          </p>
          <TrellisProfilesTable
            data={trellisProfilesQuery.data}
            isLoading={trellisProfilesQuery.isLoading}
            error={trellisProfilesQuery.error}
          />
        </VStack>
      </Accordion>

      <Accordion title={t(($) => $.onboarding["Amazon Profile Summary"])}>
        <VStack alignItems="stretch" gap="large">
          <Flex justifyContent="space-between" alignItems="center" gap="large" flexWrap="wrap">
            <p className={css({ fontSize: "large", flex: "1 1 400px" })}>
              {authorizedBy && authorizedOn ?
                <Trans
                  t={t}
                  i18nKey={($) =>
                    $.onboarding[
                      "Click the button to the right to see all profiles from Amazon associated with your Amazon Advertising Account available through the API, last authorized by <user>{name}</user> on {date}."
                    ]
                  }
                  values={{ name: authorizedBy, date: fmt.format(moment(authorizedOn).toDate()) }}
                  components={{ user: <span className={css({ textDecoration: "underline" })} /> }}
                />
              : authorizedBy ?
                <Trans
                  t={t}
                  i18nKey={($) =>
                    $.onboarding[
                      "Click the button to the right to see all profiles from Amazon associated with your Amazon Advertising Account available through the API, last authorized by <user>{name}</user>."
                    ]
                  }
                  values={{ name: authorizedBy }}
                  components={{ user: <span className={css({ textDecoration: "underline" })} /> }}
                />
              : t(
                  ($) =>
                    $.onboarding[
                      "Click the button to the right to see all profiles from Amazon associated with your Amazon Advertising Account available through the API."
                    ],
                )
              }
            </p>
            {/* The table fetches from Amazon when it mounts, and shows its own skeleton meanwhile. */}
            <HStack gap="large" flexWrap="wrap" justifyContent="flex-end">
              {!showRemoteProfiles && (
                <Button onPress={() => setShowRemoteProfiles(true)}>
                  {t(($) => $.onboarding["Check Amazon Profiles"])}
                </Button>
              )}
              {userCanImportDSPClients && (
                <Button onPress={() => setShowDSPImportModal(true)}>
                  {t(($) => $.onboarding["Import DSP Profiles"])}
                </Button>
              )}
            </HStack>
          </Flex>
          <ProfileHealthSection />
          {showRemoteProfiles && (
            <>
              <RemoteAmazonProfilesTable />
              <ExplainerBox fontSize="medium">
                <h3 className={css({ fontSize: "large", fontWeight: "semibold", margin: "0" })}>
                  {t(($) => $.onboarding["Not seeing your profile from Amazon?"])}
                </h3>
                <p>
                  {t(
                    ($) =>
                      $.onboarding[
                        'At least one campaign has to have been made in the marketplace for Amazon to share the profile. Additionally, the Amazon account connected to the Trellis platform must have "View & Edit" permissions to "Campaign Manager"'
                      ],
                  )}
                </p>
              </ExplainerBox>
            </>
          )}
        </VStack>
      </Accordion>
      <AMCInstanceSection />
      {user.is_staff && <MissingProfilesSections />}
    </>
  );
}
